import { AUTHENTICATION_TYPE, USER_CATEGORY } from '@xt/domain-model';
import { createSelector } from '@ngrx/store';
import {
  sessionInfoFeature,
  USER_ROLE,
  UserInfo,
} from '../reducers/session-info.reducer';

export const userRoleSelector = createSelector(
  sessionInfoFeature.selectUserInfo,
  (userInfo: UserInfo) => {
    if (!userInfo?.roles) return null;

    const roleMap = {
      [USER_ROLE.PATIENT]: USER_ROLE.PATIENT,
      [USER_ROLE.HCP]: USER_ROLE.PROVIDER,
      [USER_ROLE.DPO]: USER_ROLE.DATA_PRIVACY_OFFICER,
      [USER_ROLE.VERIFIED_REPRESENTATIVE]: USER_ROLE.REPRESENTATIVE,
      [USER_ROLE.PRIVATE_RECORD]: USER_ROLE.PRIVATE_RECORD,
    };

    for (const [role, mappedRole] of Object.entries(roleMap)) {
      if (userInfo.roles.includes(role as USER_ROLE)) {
        return mappedRole;
      }
    }

    return null;
  },
);

export const userRoleNewSelector = createSelector(
  sessionInfoFeature.selectUserInfo,
  (userInfo: UserInfo) => userInfo?.roles,
);

export const userCategorySelector = createSelector(
  sessionInfoFeature.selectUserInfo,
  (userInfo: UserInfo) => {
    return userInfo?.roles
      .find((value) => value.includes('xt_usercategory'))
      .replace('xt_usercategory_', '') as USER_CATEGORY;
  },
);

export const isUserRolePatientSelector = createSelector(
  userRoleNewSelector,
  (userRole) => userRole?.includes(USER_ROLE.PATIENT),
);

export const isUserRoleProviderSelector = createSelector(
  userRoleNewSelector,
  (userRole) => userRole?.includes(USER_ROLE.HCP),
);

export const isUserRoleRepresentativeSelector = createSelector(
  userRoleNewSelector,
  (userRole) => userRole?.includes(USER_ROLE.VERIFIED_REPRESENTATIVE),
);

export const isUserRoleDataPrivacyOfficerSelector = createSelector(
  userRoleNewSelector,
  (userRole) => userRole?.includes(USER_ROLE.DPO),
);

export const isUserRoleAllowedForApp = createSelector(
  userRoleNewSelector,
  (userRole) =>
    userRole.some((item) =>
      [
        USER_ROLE.HCP,
        USER_ROLE.PATIENT,
        USER_ROLE.VERIFIED_REPRESENTATIVE,
      ].includes(item),
    ),
);

export const isUserAuthentificationTypeInternal = createSelector(
  sessionInfoFeature.selectUserInfo,
  (userInfo: UserInfo) => {
    return userInfo?.authentication_type === AUTHENTICATION_TYPE.INTERNAL;
  },
);

export const isAllowAccountTerminationSelector = createSelector(
  isUserRolePatientSelector,
  isUserRoleRepresentativeSelector,
  isUserRoleProviderSelector,
  isUserRoleRepresentativeSelector,
  isUserAuthentificationTypeInternal,
  isUserRoleDataPrivacyOfficerSelector,
  (
    isUserRolePatient,
    isUserRoleRepresentative,
    isUserRoleProvider,
    isUserAuthentificationTypeInternal,
    isUserRoleDataPrivacyOfficer,
  ) => {
    return (
      isUserRolePatient ||
      isUserRoleRepresentative ||
      (isUserRoleProvider && isUserAuthentificationTypeInternal) ||
      isUserRoleDataPrivacyOfficer
    );
  },
);
